import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RedirectCommand } from '@angular/router';

@Injectable({providedIn: 'root'})
export class WorkspaceResolver implements Resolve<any> {
    private router = inject(Router);
    private async read(app: string, action: string, values: any = {}, timeout = 60000) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeout);
        try {
            const form = new FormData();
            for (const [key, value] of Object.entries(values)) form.append(key, String(value));
            const response = await fetch('/wiz/api/' + app + '/' + action, {method: 'POST', body: form, signal: controller.signal});
            return await response.json();
        } catch { return {code: 503, data: {message: '연결을 확인한 뒤 다시 시도해 주세요.'}}; }
        finally { clearTimeout(timer); }
    }
    private takeInitialDashboard() {
        const initial = (window as any).__authoringInitialDashboard;
        if (!initial) return null;
        delete (window as any).__authoringInitialDashboard;
        initial.claim();
        const age = Date.now() - initial.startedAt;
        if (age < 0 || age > 15000) { initial.cancel(); return null; }
        return initial;
    }
    async resolve(route: ActivatedRouteSnapshot) {
        // Resolve the current user before mounting the shared navigation; load page data afterwards.
        if (['dashboard', 'knowledge', 'map', 'dpo', 'admin'].includes(route.data['workspacePage'])) {
            const initial = route.data['workspacePage'] === 'dashboard' ? this.takeInitialDashboard() : null;
            const session = await this.read('page.access', 'bootstrap', {}, 10000);
            if (session.code === 401 || (session.code === 200 && !session.data?.user)) {
                initial?.cancel();
                return new RedirectCommand(this.router.parseUrl('/access/login'));
            }
            if (session.code !== 200) { initial?.cancel(); return {session, result: session}; }
            const user = session.data?.user;
            const page = route.data['workspacePage'];
            if ((['knowledge','map'].includes(page) && (user?.status !== 'active' || !['admin','researcher'].includes(user?.role))) ||
                (page === 'dpo' && (user?.status !== 'active' || !['admin','researcher','reviewer'].includes(user?.role))) ||
                (page === 'dashboard' && route.fragment === 'jobs' && (user?.status !== 'active' || !['admin','researcher'].includes(user?.role)))) {
                initial?.cancel();
                return new RedirectCommand(this.router.parseUrl('/'));
            }
            if (route.data['workspacePage'] === 'admin' && (session.data?.user?.role !== 'admin' || session.data?.user?.status !== 'active')) return new RedirectCommand(this.router.parseUrl('/'));
            return {session, result: {code: 204}, deferred: true,
                initialOverview: initial?.promise || null, cancelInitialOverview: initial?.cancel || null};
        }
        const datasets = route.data['workspacePage'] === 'datasets';
        const sources = route.data['workspacePage'] === 'sources';
        const instructionMode = datasets && route.queryParamMap.get('type') === 'instruction';
        // Query changes reuse the mounted page, which already loads its own data.
        // Detail views need their detail endpoint, not an unused list first.
        const currentPath = this.router.url?.split(/[?#]/)[0];
        const skipPreload = (sources && currentPath === '/sources') ||
            (datasets && (currentPath === '/datasets' || !!route.queryParamMap.get('id')));
        const [session, result] = await Promise.all([
            this.read('page.access', 'bootstrap'),
            (instructionMode || skipPreload) ? Promise.resolve({code:204}) : datasets ? this.read('page.datasets', 'list') : sources ? this.read('page.sources', route.queryParamMap.get('trash') === '1' ? 'trash_list' : 'browse', {
                path: route.queryParamMap.get('path') || '',
                scope: route.queryParamMap.get('scope') || 'folder',
                q: route.queryParamMap.get('q') || '',
                page: route.queryParamMap.get('page') || '1',
                work: route.queryParamMap.get('work') || '',
                show_system: route.queryParamMap.get('system') || '0'
            }) : this.read('page.dashboard', 'overview')
        ]);
        if (session.code === 401 || result.code === 401 || (session.code === 200 && !session.data?.user)) {
            return new RedirectCommand(this.router.parseUrl('/access/login'));
        }
        const user = session.data?.user;
        if (session.code === 200 && sources && (user?.status !== 'active' || !['admin','researcher'].includes(user?.role))) {
            return new RedirectCommand(this.router.parseUrl('/'));
        }
        if (session.code === 200 && datasets && user?.role === 'reviewer' && instructionMode) {
            return new RedirectCommand(this.router.parseUrl('/datasets'));
        }
        return {session, result, instructionMode, skipPreload};
    }
}
